/**
 * Utilities for DeviceModal layout and field rendering.
 *
 * Provides a `getLayout` helper that maps the selected system/device
 * to a layout definition from `data/DeviceTypeForm.json`, plus
 * small renderer helpers used by the modal components.
 */
import layout from 'data/DeviceTypeForm.json';
import { getLicensePool } from '../../../../../config/licensepools.config';
export { renderField } from './modalDropdown.utils';

/**
 * Resolve the layout config for the currently-selected device type.
 *
 * `formData.device_type` is the device type name, which keys directly into the
 * `data/DeviceTypeForm.json` layout map.
 *
 * @param {object} formData - The local form state (expects `device_type` and `system_type`).
 * @param {Array<object>} deviceTypes - Structured device types array generated by the UI.
 * @returns {object|null} The layout config for the selected device type, or `null` when not found.
 */
// eslint-disable-next-line no-unused-vars
export const getLayout = (formData, deviceTypes) => {
  if (!formData?.system_type) return null;

  const key = formData.device_type?.toLowerCase().trim();
  if (!key) return null;

  return layout[key] || null;
};

// Helper renderers (`renderField`, `renderInputField`, `renderValidDevice`)
// were moved to `modalDropdown.utils.jsx` and are re-exported above.

// Limit checker uses the helper
export const isLimitReached = (formData, deviceTypes, systemCount) => {
  const selectedTypeInfo = getSelectedType(formData);
  const selectedSystemType = getSelectedSystemType(formData);
  return getDeviceType(selectedTypeInfo, systemCount, selectedSystemType).limitReached;
};

export const resolveSubmittedDeviceId = (device) =>
  device?.new_node_id ?? device?.node_id ?? device?.device?.node_id ?? null;

export const hasDuplicateDeviceId = (device, connectedDevices = []) => {
  const submittedDeviceId = resolveSubmittedDeviceId(device);
  if (submittedDeviceId === null || submittedDeviceId === undefined || submittedDeviceId === '') {
    return false;
  }

  const currentId = device?.id;
  return connectedDevices.some(
    (connectedDevice) =>
      String(connectedDevice?.node_id) === String(submittedDeviceId) &&
      String(connectedDevice?.id) !== String(currentId),
  );
};

export const getDeviceIdAlreadyInUseMessage = (deviceId) =>
  deviceId === null || deviceId === undefined || deviceId === ''
    ? 'Device ID is already in use.'
    : `Device ID ${deviceId} is already in use.`;

const collectMessageText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(collectMessageText).filter(Boolean).join(' ');
  if (typeof value !== 'object') return '';

  return [
    value.message,
    value.msg,
    value.detail,
    value.error,
    value.title,
    value.raw,
    value.data,
    value.errors,
    value.result,
    value.response?.data,
  ]
    .map(collectMessageText)
    .filter(Boolean)
    .join(' ');
};

export const resolveDeviceMutationMessage = (error, deviceId) => {
  const errorText = collectMessageText(error).toLowerCase();
  const hasDuplicateLanguage = errorText.includes('duplicate') || errorText.includes('already');
  const hasDeviceIdLanguage =
    errorText.includes('device id') ||
    errorText.includes('node id') ||
    errorText.includes('node_id') ||
    errorText.includes('new_node_id');
  const isDuplicateDeviceId =
    (error?.status === 409 && hasDuplicateLanguage) ||
    (hasDuplicateLanguage && hasDeviceIdLanguage);

  return isDuplicateDeviceId ? getDeviceIdAlreadyInUseMessage(deviceId) : '';
};

// Both selections are stored as the type names themselves.
const getSelectedType = (formData) => formData?.device_type || null;

const getSelectedSystemType = (formData) => formData?.system_type || null;

const getDeviceType = (selected = getSelectedType(), systemCount, selectedSystemType) => {
  const counts = systemCount || {};
  const pool = getLicensePool(selectedSystemType, selected);

  if (pool === 'virtual') {
    return {
      key: 'virtual',
      label: selected || 'Virtual Device',
      max: null,
      remaining: null,
      limitReached: false,
    };
  }
  if (pool === 'camera') {
    const remaining = Number(counts?.remaining_connected_cameras ?? 0);
    return {
      key: 'camera',
      label: selected || 'Camera',
      max: counts?.connected_cameras ?? 0,
      remaining,
      limitReached: remaining <= 0,
    };
  }
  // devices bucket = general connected devices; remaining count is backend-authoritative
  const remaining = Number(counts?.remaining_connected_devices ?? 0);
  return {
    key: 'devices',
    label: selected || 'Device',
    max: counts?.connected_devices ?? 0,
    remaining,
    limitReached: remaining <= 0,
  };
};
